import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Box, Flex, Text, Table, Badge, Grid, Card } from "@radix-ui/themes"
import { restaurantPortalAPI } from "../../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../../contexts/RestaurantAuthContext"
import ReportLayout from "./ReportLayout"
import LoadingSpinner from "../dashboard/LoadingSpinner"
import { Input } from "../../ui/input"

interface DailySalesData {
    date: string
    totalRevenue: number
    totalTax: number
    paymentBreakdown: Record<string, { revenue: number; tax: number; count: number }>
    enabledPaymentMethods: string[]
}

const DailySalesReport: React.FC = () => {
    const { restaurantId } = useParams()
    const { user } = useRestaurantAuth()
    const navigate = useNavigate()
    const [data, setData] = useState<DailySalesData | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [reportDate, setReportDate] = useState<string>(new Date().toISOString().split('T')[0])

    useEffect(() => {
        fetchReport()
    }, [restaurantId, reportDate])

    const fetchReport = async () => {
        try {
            setLoading(true)
            const response = await restaurantPortalAPI.getDailySalesReport(restaurantId!, reportDate)
            setData(response.data.data)
            setError(null)
        } catch (err: any) {
            console.error("Failed to fetch daily sales report", err)
            setError("Failed to load report data.")
        } finally {
            setLoading(false)
        }
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "SDG",
        }).format(amount)
    }

    if (loading && !data) {
        return <LoadingSpinner size="3" text="Generating Daily Sales Report..." />
    }

    if (error) {
        return (
            <Box p="6" className="text-center">
                <Text color="red">{error}</Text>
                <Box mt="4">
                    <button onClick={() => navigate(-1)} className="text-blue-600 underline">Go Back</button>
                </Box>
            </Box>
        )
    }

    if (!data) return null

    return (
        <Box>
            <Box className="print:hidden mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                <Flex gap="4" align="center">
                    <Text weight="medium">Select Date:</Text>
                    <Input
                        type="date"
                        value={reportDate}
                        onChange={(e) => setReportDate(e.target.value)}
                        className="w-48"
                    />
                </Flex>
            </Box>

            <ReportLayout
                title="Daily Sales Summary"
                subtitle="End of Day Revenue & Payment Method Reconciliation"
                restaurantName={user?.restaurant?.name}
                dateRange={new Date(reportDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                onBack={() => navigate(`/restaurant/${restaurantId}/reports`)}
            >
                <Grid columns="2" gap="6" mb="8">
                    <Card className="bg-gray-50 print:bg-white print:border print:border-gray-300">
                        <Text size="2" color="gray" className="uppercase tracking-wider font-bold mb-1">Gross Revenue</Text>
                        <Text size="8" weight="bold" className="text-gray-900">{formatCurrency(data.totalRevenue)}</Text>
                    </Card>
                    <Card className="bg-gray-50 print:bg-white print:border print:border-gray-300">
                        <Text size="2" color="gray" className="uppercase tracking-wider font-bold mb-1">Tax Collected</Text>
                        <Text size="8" weight="bold" className="text-gray-900">{formatCurrency(data.totalTax)}</Text>
                    </Card>
                </Grid>

                <Box mb="6">
                    <Text size="4" weight="bold" mb="4" className="block border-b pb-2">Payment Method Breakdown</Text>
                    <Table.Root variant="surface" className="print:border print:border-gray-300">
                        <Table.Header className="print:bg-gray-100">
                            <Table.Row>
                                <Table.ColumnHeaderCell>Payment Origin</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-center">Tickets</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Tax</Table.ColumnHeaderCell>
                                <Table.ColumnHeaderCell className="text-right">Total Generated</Table.ColumnHeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {Object.entries(data.paymentBreakdown).map(([method, stats]) => (
                                <Table.Row key={method}>
                                    <Table.Cell>
                                        <Flex gap="2" align="center">
                                            <Text className="capitalize font-medium">{method.replace('_', ' ')}</Text>
                                            {data.enabledPaymentMethods.includes(method) ? (
                                                <Badge color="green" variant="soft" className="print:border print:border-green-300">Active</Badge>
                                            ) : (
                                                <Badge color="yellow" variant="soft" className="print:border print:border-yellow-300">Legacy / Offline</Badge>
                                            )}
                                        </Flex>
                                    </Table.Cell>
                                    <Table.Cell className="text-center">{stats.count}</Table.Cell>
                                    <Table.Cell className="text-right text-gray-500">{formatCurrency(stats.tax)}</Table.Cell>
                                    <Table.Cell className="text-right font-bold">{formatCurrency(stats.revenue)}</Table.Cell>
                                </Table.Row>
                            ))}
                            {Object.keys(data.paymentBreakdown).length === 0 && (
                                <Table.Row>
                                    <Table.Cell colSpan={4} className="text-center py-8 text-gray-500">
                                        No sales recorded for this date.
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table.Root>
                </Box>
            </ReportLayout>
        </Box>
    )
}

export default DailySalesReport
